/**
 * Combo: dos SKUs de stock, mismo group id, misma cantidad.
 * F5 interno; F6 visual: una línea «A + B» con suma de precios.
 * F7: presupuesto→venta conserva el grupo; NC Cant. N = N de cada SKU.
 * No crea un tercer producto ni usa ítem personalizado.
 * Run: npx tsx --test src/lib/comboLine.test.ts
 */
import { toNum } from "./utils";
import { productoEsPersonalizado } from "./ventaStockDisponible";

export type ComboCapableLine = {
  lineKey: string;
  comboGroup?: string | null;
  id?: number | null;
  codigo: string;
  nombre: string;
  precioBase: number;
  cantidad: number;
  descuento1: number;
  descuento2: number;
  ivaPorcentaje?: number | null;
};

export type ComboStockProduct = {
  id?: number;
  codigo: string;
  nombre: string;
  precioFinal?: number;
  precio_final?: number;
  precioBase?: number;
  precio_base?: number | string;
  esPersonalizado?: boolean;
  es_personalizado?: boolean;
  ivaPorcentaje?: number;
  iva_porcentaje?: number | string;
};

export function newComboGroupId(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function comboLineKey(groupId: string, productId: number): string {
  return `combo-${groupId}-${productId}`;
}

export function regularLineKey(productId: number): string {
  return `prod-${productId}`;
}

export function lineKeyOf(line: { lineKey?: string; id?: number | null; codigo?: string }): string {
  if (line.lineKey) return line.lineKey;
  if (line.id != null && line.id > 0) return regularLineKey(line.id);
  return line.codigo || "";
}

export const COMBO_NAME_JOIN = " + ";
/** Código en grilla: un renglón por SKU, sin +. */
export const COMBO_CODIGO_STACK = "\n";

export function comboGroupOf(line: {
  comboGroup?: string | null;
  combo_group?: string | null;
}): string | null {
  const g = line.comboGroup ?? line.combo_group;
  if (g == null || g === "") return null;
  return String(g);
}

export type ComboVisualRow<T> = {
  key: string;
  comboGroup: string | null;
  legs: T[];
  isCombo: boolean;
};

export type PresupuestoVentaLine = ComboCapableLine & {
  deferred?: boolean;
  configSnapshot?: Record<string, unknown> | null;
  configHash?: string | null;
};

/** F7: «usar en venta» copia combo_group y qty 1:1 del presupuesto. */
export function presupuestoItemsToVentaLines(
  items: Array<Record<string, unknown>> | undefined | null,
): PresupuestoVentaLine[] {
  return (items || []).map((item, idx) => {
    const configSnapshot = (item.config_snapshot || item.configSnapshot || null) as
      | Record<string, unknown>
      | null;
    const configHash = (item.config_hash || item.configHash || null) as string | null;
    const productoId = item.producto ?? item.productoId ?? null;
    const isDeferred = !productoId && !!(configSnapshot || configHash);
    const syntheticId = isDeferred
      ? -(Number(item.id) || idx + 1)
      : Number(productoId || item.id);
    const comboGroup = comboGroupOf(item as { comboGroup?: string | null; combo_group?: string | null });
    const lineKey = comboGroup
      ? comboLineKey(comboGroup, Number(syntheticId))
      : isDeferred
        ? `deferred-${syntheticId}`
        : regularLineKey(Number(productoId || syntheticId));
    const ivaRaw = item.iva_porcentaje ?? item.ivaPorcentaje;
    return {
      id: syntheticId,
      codigo: String(
        item.producto_codigo ||
          item.codigo_producto ||
          item.codigoProducto ||
          item.codigo ||
          "CODE",
      ),
      nombre: String(
        item.producto_nombre ||
          item.nombre_producto ||
          item.nombreProducto ||
          item.nombre ||
          "Producto",
      ),
      precioBase: toNum(
        item.precio_unitario || item.precioUnitario || item.precio_base || item.precioBase,
      ),
      cantidad: Math.floor(Math.max(1, toNum(item.cantidad))),
      descuento1: toNum(item.descuento1),
      descuento2: toNum(item.descuento2),
      deferred: isDeferred,
      configSnapshot: isDeferred ? configSnapshot : null,
      configHash: isDeferred ? configHash : null,
      comboGroup,
      lineKey,
      ivaPorcentaje:
        ivaRaw != null && ivaRaw !== ""
          ? toNum(ivaRaw as number | string)
          : null,
    };
  });
}

export function groupComboVisualRows<T>(
  lines: T[],
  groupOf: (line: T) => string | null = (line) => comboGroupOf(line as never),
  keyOf: (line: T) => string = (line) => lineKeyOf(line as never),
): ComboVisualRow<T>[] {
  const byGroup = new Map<string, T[]>();
  for (const line of lines) {
    const g = groupOf(line);
    if (!g) continue;
    const arr = byGroup.get(g) || [];
    arr.push(line);
    byGroup.set(g, arr);
  }
  const seen = new Set<string>();
  const rows: ComboVisualRow<T>[] = [];
  for (const line of lines) {
    const g = groupOf(line);
    if (!g) {
      rows.push({ key: keyOf(line), comboGroup: null, legs: [line], isCombo: false });
      continue;
    }
    if (seen.has(g)) continue;
    seen.add(g);
    const legs = byGroup.get(g) || [line];
    if (legs.length === 2) {
      rows.push({ key: keyOf(legs[0]), comboGroup: g, legs, isCombo: true });
      continue;
    }
    // Par incompleto: no inventar la pata; renglones sueltos (F6).
    // F7: si las dos internas están en `lines`, nunca se llega acá.
    for (const leg of legs) {
      rows.push({ key: keyOf(leg), comboGroup: g, legs: [leg], isCombo: false });
    }
  }
  return rows;
}

function _legNombre(leg: {
  nombre?: string;
  nombreProducto?: string;
  nombre_producto?: string;
}): string {
  return String(leg.nombre || leg.nombreProducto || leg.nombre_producto || "").trim();
}

function _legCodigo(leg: {
  codigo?: string;
  codigoProducto?: string;
  codigo_producto?: string;
}): string {
  return String(leg.codigo || leg.codigoProducto || leg.codigo_producto || "").trim();
}

export function comboVisualNombre(
  legs: Array<{
    nombre?: string;
    nombreProducto?: string;
    nombre_producto?: string;
  }>,
): string {
  return legs.map(_legNombre).filter(Boolean).join(COMBO_NAME_JOIN);
}

function _legComboNombrePdf(leg: {
  combo_nombre_pdf?: string | null;
  comboNombrePdf?: string | null;
}): string {
  return String(leg.combo_nombre_pdf || leg.comboNombrePdf || "").trim();
}

/** Etiqueta PDF del combo: override del grupo, o concat «A + B». */
export function comboVisualNombrePdf(
  legs: Array<{
    nombre?: string;
    nombreProducto?: string;
    nombre_producto?: string;
    combo_nombre_pdf?: string | null;
    comboNombrePdf?: string | null;
  }>,
): string {
  for (const leg of legs) {
    const override = _legComboNombrePdf(leg);
    if (override) return override;
  }
  return comboVisualNombre(legs);
}

/** Nombre que se imprime: override de la línea, o el nombre del alta. */
export function lineNombreImpreso(line: {
  nombre?: string | null;
  nombrePdf?: string | null;
  nombre_pdf?: string | null;
}): string {
  const override = String(line.nombrePdf || line.nombre_pdf || "").trim();
  return override || String(line.nombre || "").trim();
}

/**
 * Ítem persistido para PATCH de nombre PDF.
 * Combo sin ids (borrador) → null (no mostrar input).
 */
export function comboVisualPersistedSaveId<T extends { id?: number | string | null }>(
  row: ComboVisualRow<T>,
): number | null {
  const ids = row.legs
    .map((leg) => Number(leg.id))
    .filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) return null;
  if (row.isCombo && ids.length !== row.legs.length) return null;
  return ids[0];
}

export function comboVisualCodigoLines(
  legs: Array<{
    codigo?: string;
    codigoProducto?: string;
    codigo_producto?: string;
  }>,
): string[] {
  return legs.map(_legCodigo).filter(Boolean);
}

export function comboVisualCodigo(
  legs: Array<{
    codigo?: string;
    codigoProducto?: string;
    codigo_producto?: string;
  }>,
): string {
  return comboVisualCodigoLines(legs).join(COMBO_CODIGO_STACK);
}

export function comboVisualPrecioSum(
  legs: Array<{
    precioBase?: number;
    precio_unitario?: number | string;
    precioUnitario?: number | string;
  }>,
): number {
  return legs.reduce(
    (s, l) => s + toNum(l.precioBase ?? l.precio_unitario ?? l.precioUnitario),
    0,
  );
}

export function syncComboPatch<T extends {
  lineKey?: string;
  comboGroup?: string | null;
  combo_group?: string | null;
  id?: number | null;
  codigo?: string;
}>(
  lines: T[],
  key: string,
  patch: Partial<T>,
): T[] {
  const target = lines.find((l) => lineKeyOf(l) === key);
  if (!target) return lines;
  const group = comboGroupOf(target);
  if (group) {
    return lines.map((l) => (comboGroupOf(l) === group ? { ...l, ...patch } : l));
  }
  return lines.map((l) => (lineKeyOf(l) === key ? { ...l, ...patch } : l));
}

/** IVA 21 si el producto tiene IVA; 0 si está exento. */
export function comboIvaPorcentaje(product: {
  ivaPorcentaje?: number;
  iva_porcentaje?: number | string | null;
}): number {
  const raw = product.ivaPorcentaje ?? product.iva_porcentaje;
  if (raw == null || raw === "") return 21;
  const n = Number(raw);
  if (!Number.isFinite(n) || n > 0) return 21;
  return 0;
}

export function validateComboPicks(
  a: ComboStockProduct | null,
  b: ComboStockProduct | null,
): string | null {
  if (!a?.id || !b?.id) return "Elegí dos productos de stock.";
  if (a.id === b.id) return "Los dos productos tienen que ser distintos.";
  if (productoEsPersonalizado(a) || productoEsPersonalizado(b)) {
    return "El combo no usa ítem personalizado.";
  }
  return null;
}

export function buildComboLines(
  a: ComboStockProduct,
  b: ComboStockProduct,
  qty: number,
  groupId?: string,
  priceSource: "venta" | "base" = "venta",
): ComboCapableLine[] {
  const err = validateComboPicks(a, b);
  if (err) throw new Error(err);
  const group = groupId || newComboGroupId();
  const n = Math.floor(Math.max(1, qty));
  const toLine = (p: ComboStockProduct): ComboCapableLine => ({
    lineKey: comboLineKey(group, p.id as number),
    comboGroup: group,
    id: p.id as number,
    codigo: p.codigo,
    nombre: p.nombre,
    precioBase: priceSource === "base"
      ? toNum(p.precioBase ?? p.precio_base)
      : toNum(p.precioFinal ?? p.precio_final ?? p.precioBase ?? p.precio_base),
    cantidad: n,
    descuento1: 0,
    descuento2: 0,
    ivaPorcentaje: comboIvaPorcentaje(p),
  });
  return [toLine(a), toLine(b)];
}

export function removeLinesByKey<T extends { lineKey?: string; comboGroup?: string | null; combo_group?: string | null; id?: number | null; codigo?: string }>(
  lines: T[],
  key: string,
): T[] {
  const target = lines.find((l) => lineKeyOf(l) === key);
  if (!target) return lines;
  const group = comboGroupOf(target);
  if (group) {
    return lines.filter((l) => comboGroupOf(l) !== group);
  }
  return lines.filter((l) => lineKeyOf(l) !== key);
}

export function syncComboCantidad<T extends { lineKey?: string; comboGroup?: string | null; combo_group?: string | null; cantidad: number; id?: number | null; codigo?: string }>(
  lines: T[],
  key: string,
  cantidad: number,
): T[] {
  const qty = Math.floor(Math.max(1, cantidad));
  const target = lines.find((l) => lineKeyOf(l) === key);
  if (!target) return lines;
  const group = comboGroupOf(target);
  if (group) {
    return lines.map((l) =>
      comboGroupOf(l) === group ? { ...l, cantidad: qty } : l,
    );
  }
  return lines.map((l) => (lineKeyOf(l) === key ? { ...l, cantidad: qty } : l));
}

export function findRegularLine<T extends { comboGroup?: string | null; combo_group?: string | null; id?: number | null; codigo?: string }>(
  lines: T[],
  product: { id?: number; codigo?: string },
): T | undefined {
  return lines.find((l) => {
    if (comboGroupOf(l)) return false;
    if (product.id != null && l.id != null) return l.id === product.id;
    if (product.codigo && l.codigo) return l.codigo === product.codigo;
    return false;
  });
}

export type RemitoComboItem = {
  id?: number | string | null;
  codigo?: string;
  codigoProducto?: string;
  codigo_producto?: string;
  comboGroup?: string | null;
  combo_group?: string | null;
  cantidad?: number;
  cantidadEntregada?: number;
  cantidad_entregada?: number;
  entregado?: boolean;
};

export function remitoComboGroupOf(item: {
  comboGroup?: string | null;
  combo_group?: string | null;
}): string | null {
  return comboGroupOf(item);
}

export type RemitoComboLegTarget<T> = {
  item: T;
  itemId: number;
  nombre: string;
};

/**
 * F2: click targets de cada pata del combo, con el item_id real.
 * No parte «A + B» ni inventa un padre. Null si no es par de 2 o falta id.
 */
export function remitoComboLegClickTargets<T extends {
  id?: number | string | null;
  nombre?: string;
  nombreProducto?: string;
  nombre_producto?: string;
}>(row: ComboVisualRow<T>): RemitoComboLegTarget<T>[] | null {
  if (!row.isCombo || row.legs.length !== 2) return null;
  const targets: RemitoComboLegTarget<T>[] = [];
  for (const leg of row.legs) {
    const id = Number(leg.id);
    if (!Number.isFinite(id) || id <= 0) return null;
    targets.push({ item: leg, itemId: id, nombre: _legNombre(leg) });
  }
  return targets;
}

/**
 * F7: Cant. N de la línea visual = N de cada SKU (mapa por id interno).
 * `reduce: "min"` para prefill; `"max"` al aplicar el N que eligió el usuario.
 */
export function expandNcComboQtyMap<T extends {
  id?: number;
  comboGroup?: string | null;
  combo_group?: string | null;
}>(
  items: T[] | undefined,
  map: Record<number, number>,
  reduce: "min" | "max" = "max",
): Record<number, number> {
  const next: Record<number, number> = { ...map };
  const rows = groupComboVisualRows(items || []);
  for (const row of rows) {
    if (!row.isCombo) continue;
    const ids = row.legs
      .map((leg) => Number((leg as { id?: number }).id))
      .filter((id) => Number.isFinite(id) && id > 0);
    const present = ids.map((id) => next[id]).filter((n) => n != null && !Number.isNaN(n));
    if (!present.length) continue;
    const n = reduce === "min" ? Math.min(...present) : Math.max(...present);
    for (const id of ids) next[id] = n;
  }
  return next;
}

export function remitoItemMatchesId(item: RemitoComboItem, itemId: number | string): boolean {
  return (
    item.id === itemId ||
    item.codigo === itemId ||
    item.codigoProducto === itemId ||
    item.codigo_producto === itemId
  );
}

/**
 * F5 remito parcial 1:1: Cant. / entregado de una pata se copia a la otra.
 * Permite 0 (a diferencia de la grilla de factura). F6 agrupa la grilla visual.
 */
export function syncRemitoComboEntrega<T extends RemitoComboItem>(
  items: T[],
  itemId: number | string,
  patch: { cantidadEntregada?: number; entregado?: boolean },
): T[] {
  const target = items.find((it) => remitoItemMatchesId(it, itemId));
  if (!target) return items;
  const group = remitoComboGroupOf(target);
  const same = (it: T) =>
    group ? remitoComboGroupOf(it) === group : remitoItemMatchesId(it, itemId);
  const qty =
    patch.cantidadEntregada == null
      ? null
      : Math.max(0, Math.floor(patch.cantidadEntregada));
  return items.map((it) => {
    if (!same(it)) return it;
    return {
      ...it,
      ...(qty != null
        ? { cantidadEntregada: qty, cantidad_entregada: qty, cantidad: qty }
        : {}),
      ...(patch.entregado != null ? { entregado: patch.entregado } : {}),
    };
  });
}
